/* eslint-disable */
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _unset from 'lodash/unset';
import { post, ruid, sortOrderLines } from '@/lib/utils';
import { serializeSave } from '@/lib/serializers';
import {
  defaultKeys,
  updateValue,
  updateMultipleValues,
  addCollection,
  removeCollection,
  updateCollection,
  updateCollectionMultipleValues,
  updateCollectionUniqueValues,
  update_object,
  update_multiple_values,
  add_collection,
  remove_collection,
  update_collection,
  update_collection_multiple_values,
  update_collection_unique_values,
  insertKeys as insertKeysUtil,
  pruneDeletedObjects,
  removeMessages,
  remove_messages,
} from '@/lib/vuexUtils';

const INCOMING_MODEL_INIT = {
  // NOTE: There are generally better places to modify a model
  // for the client's internal purposes.  Generally, the mechanism
  // here shouldn't be needed.
  CustomerOrder: (obj) => {
    // Lines aren't sorted until the Order object is pulled up on the screen
    // which then mutates the object.  So, sorting lines needs to happen before
    // dirty tracking, at least until #14381 (but also must happen before user
    // starts interacting with a CustomerOrder).  See #14578
    if (obj && obj.orders) {
      for (const o of obj.orders) {
        if (o.orderlines) o.orderlines = sortOrderLines(o, o.orderlines, true).sortedLines;
        if (o.adjustments) {
          for (const a of o.adjustments) {
            if (a.orderlines) a.orderlines = sortOrderLines(a, a.orderlines, true).sortedLines;
          }
        }
      }
    }
  },
};

const initializeIncomingModel = (record) => {
  if (record && typeof record === 'object' && record._classname) {
    const fn = INCOMING_MODEL_INIT[record._classname];
    if (fn) fn(record);
  }
};

const localState = {
  panelExpanded: false,
  record: {},
  newOrderCustomer: null,
  skipDirtyKeys: {
    CustomerOrder: ['_orderdate', '_ordersiteid'],
    Order: ['_isSameShipping'],
    OrderLine: ['_minLineNumber', '_minServerSequence', '_valueProductId', 'sequenceadded'],
  },
  className: null,
  selectedRecordIds: [],
  saveReminder: false,
  initialRecord: null,
  saveInFlight: false,
  autoUpdateInFlight: false,
  searchInFlight: false,
  isRecordDirty: false,
  autoUpdateManageModels: {},
  autoUpdateChanges: [],
  autoUpdateIDs: {},
  autoUpdatePath: null,
  prevUpdatePaths: [],
  showContextMenu: false,
  contextMenuItems: [],
  contextMenuPosition: { x: 0, y: 0 },
  copyRecordConfig: null,
};

const localGetters = {
  record: state => {
    return state.record;
  },

  newOrderCustomer: state => state.newOrderCustomer,

  permissions: state => {
    return state.permissions;
  },

  class_name: state => {
    return state.className;
  },

  selectedRecords: state => model => {
    return model.filter((item) => {
      return state.selectedRecordIds.some(id => id === item._id);
    });
  },
  selectedRecordIds: state => state.selectedRecordIds,

  save_reminder: state => {
    return state.saveReminder;
  },

  skip_dirty_keys: state => className => {
    return _get(state, `skipDirtyKeys[${className}]`, []);
  },

  isRecordDirty: state => state.isRecordDirty,
  saveInFlight: state => state.saveInFlight,
  searchInFlight: state => state.searchInFlight,
  autoUpdateInFlight: state => state.autoUpdateInFlight,
  autoUpdatePath: state => state.autoUpdatePath,
  autoUpdateChanges: state => state.autoUpdateChanges,
  contextMenuItems: state => state.contextMenuItems,
  contextMenuPosition: state => state.contextMenuPosition,
  showContextMenu: state => state.showContextMenu,
  copyRecordConfig: state => state.copyRecordConfig,
};

const localActions = {
  updateValue,

  updateMultipleValues,

  updateCollection,

  updateCollectionMultipleValues,

  updateCollectionUniqueValues,

  addCollection,

  removeCollection,

  removeMessages,

  setClassName({ commit }, className) {
    commit('class_name', className);
  },

  setModel({ commit }, { model, resetDirtyTracking = false }) {
    commit('set_model', model);

    // NOTE: Do not use this flag without good reason, it currently exists to avoid the need to refactor very old screens (2-3 years old).
    // If you have a need for it, it could be a sign that you're working round an existing flow (e.x. determineParamHandler, defaultSave, or a state updating action).
    // One use case is outside of saves and route transition where a model/record needs wholesale replacement (see disbursments).
    if (resetDirtyTracking) commit('set_dirty_flag', false);
  },

  setManageModel({ commit }, { model, className = null }) {
    commit('set_manage_model', { className: className || model._classname, model });
  },

  setUpdatePath({ commit }, { basePath, saveLast = false }) {
    commit('set_update_path', { basePath, saveLast });
  },

  updateRecord({ commit, rootState }, model) {
    initializeIncomingModel(model);
    commit('update_record', { record: model, metaState: rootState.meta });
  },

  setCachedModel({ commit }, { model, className }) {
    commit('set_model', _cloneDeep(model)); // Not cloning will cause issues with localstorage
    commit('class_name', className);
  },

  setSelectedRecordIds({ commit }, records) {
    const recordIds = records.reduce((acc, record) => {
      return record && record._id ? acc.concat(record._id) : acc;
    }, []);
    commit('set_selected_record_ids', recordIds);
  },

  async get({ commit, rootGetters, rootState, dispatch }, urlParams) {
    const params = new URLSearchParams();
    const keys = rootGetters['meta/primary_keys'](urlParams.className);
    const pk = keys.pop();

    params.append(pk, urlParams.params.id);
    keys.forEach((key) => {
      params.append(key, urlParams.query[key]);
    });

    let results;
    if (urlParams.className === 'CustomerOrder') {
      const response = await dispatch('query', { url: `/tg/customerorder/get?orderid=${urlParams.params.id}` });
      results = response.results;
    } else {
      const response = await dispatch('query', { url: `/tg/${urlParams.className.toLowerCase()}/get?${params}` });
      results = response.results;
    }
    initializeIncomingModel(results[0]);
    // Still call a post here for flex: true
    commit('update_record', { record: results[0], metaState: rootState.meta });
  },

  async associationQuery({ rootState, dispatch }, className) {
    const { results } = await dispatch('query', { url: `/tg/${className.toLowerCase()}/query` });
    results.forEach((item, index) => {
      results[index] = insertKeysUtil(defaultKeys, item, rootState.meta);
    });
    return results;
  },

  async save({ rootGetters, state, dispatch, commit }, className) {
    const url = `/tg/${className.toLowerCase()}/save`;
    let stagedRecord = _cloneDeep(state.record);
    // REFACTOR: revert to mutation way of doing this, on failed request, rollback state
    stagedRecord = pruneDeletedObjects(stagedRecord);
    const meta = rootGetters['meta/class_schema'](className);
    stagedRecord = await serializeSave({ record: stagedRecord, className, meta });
    const params = { [className]: stagedRecord };

    let results = null;
    let hasErrors = null;
    let hasWarnings = null;
    try {
      commit('set_save_in_flight', true);
      const record = await post(url, params);
      results = _get(record, 'results[0]');
      hasErrors = _get(record, 'errors');
      hasWarnings = _get(record, 'warnings');

      if (!record || !results) commit('set_save_in_flight', false);

      if (!record) {
        await dispatch('notify/push', {
          title: 'Save Error',
          text: 'An error occurred while attempting to save the current record.',
        }, { root: true });
        return { results, hasErrors, hasWarnings };
      }

      if (!results) return { results, hasErrors, hasWarnings };

      // update record in cached search results
      if (!hasErrors) {
        dispatch('cached/updateCachedSearchRecords', { className, records: [results], path: `/${className}`, shouldInsertKeys: true }, { root: true });
        dispatch('cached/clearStaleDataproviders', className, { root: true });
        if (className === 'GlAccount') dispatch('glAccount/clearStaleGlAccounts', {}, { root: true }); // GL Accounts don't use a dataprovider so a seperate dispatch is required.
      }
    } finally {
      commit('set_save_in_flight', false);
    }

    return { results, hasErrors, hasWarnings };
  },


  async dataProvider({ rootState, dispatch }, dataProvider) {
    const { results } = await dispatch('post', { url: `/${dataProvider.url}`, params: dataProvider.httpParams });

    results.forEach((item, index) => {
      results[index] = insertKeysUtil(defaultKeys, item, rootState.meta);
    });

    return results;
  },
  async getUserCopyOptions(ctx, cls) {
    const { results } = await post('/tg/system/get_user_copy_options', { cls });
    return results;
  },
  async postUserCopy({ rootState, dispatch }, { params, copyFlds }) {
    const { results, errors } = await post(`/tg/${params.clsname.toLowerCase()}/user_copy`, params);
    if (errors) {
      let [title, text] = results.pop();
      if (title) {
        // translate error field to its label:
        if (copyFlds) {
          const entry = copyFlds.find(({ name }) => name === title);
          if (entry) title = entry.label;
        }
      }
      await dispatch('notify/push', {
        title: title || 'Copy Error',
        text,
      }, { root: true });
      return null;
    }
    insertKeysUtil(defaultKeys, results[0], rootState.meta);
    return results[0];
  },

  saveReminder({ commit }, reminderState) {
    commit('save_reminder', reminderState);
  },

  rollbackState({ commit }, prevoiusState) {
    commit('rollback_state', prevoiusState);
  },

  async post({ commit }, { url, params, returnResponse, additionalConfig, asSaveInFlight = false }) {
    if (asSaveInFlight) commit('set_save_in_flight', true);
    const { results, errors, response } = await post(url, params, returnResponse, additionalConfig);
    if (asSaveInFlight) commit('set_save_in_flight', false);
    return { results, errors, response };
  },

  async query({ rootState, rootGetters, dispatch, commit }, { url, params, additionalConfig, insertKeys = false, suppressUserFeedback = false }) {
    try {
      if (!suppressUserFeedback && !rootGetters['editObject/searchInFlight']) commit('set_search_in_flight', true);
      const payload = { url, params, returnResponse: true, additionalConfig };
      const res = await dispatch('post', payload);

      const { results, errors, response } = res;
      const { limit, offset, recordcount } = response;

      if (insertKeys) {
        for (const item of results) {
          insertKeysUtil(defaultKeys, item, rootState.meta);
        }
      }

      return {
        results,
        errors,
        pagination: {
          limit,
          offset,
          recordCount: recordcount,
        },
      };
    } finally {
      if (!suppressUserFeedback || rootGetters['editObject/searchInFlight']) commit('set_search_in_flight', false);
    }
  },

  async delete({ rootGetters, dispatch }, { className, record }) {
    const url = `/tg/${className.toLowerCase()}/delete`;
    const primaryKeys = rootGetters['meta/primary_keys'](className);
    const params = {};

    for (const key of primaryKeys) {
      params[key] = record[key];
    }

    const { results, errors } = await dispatch('post', { url, params });

    dispatch('cached/removeRecordsFromCachedSearch',
      { className, path: `/${className}`, records: [record] },
      { root: true },
    );

    if (rootGetters['cached/hasCachedRecord'](className)) {
      dispatch('cached/invalidateScreenDataByClassName', className, { root: true });
    }

    return { results, errors };
  },

  async fetchUpdates({ commit, state }, { model, alsoManaging, responseCallback, meta = null }) {
    const className = model._classname;
    const autoUpdateID = ruid();
    commit('set_update_in_flight', { flag: true, className, autoUpdateID });

    const insertKeyPayload = [
      { key: '_id', defaultValue: null, isPrimaryKey: true },
      { key: 'uid', defaultValue: null, isPrimaryKey: false },
    ];

    model = insertKeysUtil(insertKeyPayload, model, meta);

    const clonedModel = _cloneDeep(model);
    const updateUrl = `/tg/${className.toLowerCase()}/get_updates`;

    const params = {};
    params.also_managing = [...alsoManaging];
    params[className] = await serializeSave({ record: clonedModel, className });
    const updateResp = await post(updateUrl, params, true, { autoUpdateID });
    if (state.autoUpdateIDs[className] === autoUpdateID) {
      responseCallback(updateResp, clonedModel);
      commit('set_update_in_flight', { flag: false, className });
    }
  },

  addUpdateChanges({ commit }, changeset) {
    commit('add_update_changes', changeset);
  },

  setSearchInFlight({ commit }, bool) {
    commit('set_search_in_flight', bool);
  },

  setAutoUpdateFlag({ commit }, flag) {
    commit('set_auto_update_flag', flag);
  },

  setRecordIsDirty({ commit }, flag) {
    commit('set_dirty_flag', flag);
  },

  toggleContextMenu({ commit }, payload) {
    commit('toggle_context_menu', payload);
  },

  setCopyRecordConfig({ commit }, config) {
    commit('set_copy_record_config', config);
  },
};

const localMutations = {
  update_object,

  update_multiple_values,

  update_collection,

  update_collection_multiple_values,

  update_collection_unique_values,

  add_collection,

  remove_collection,

  remove_messages,

  update_record(state, { record, metaState }) {
    state.record = insertKeysUtil(defaultKeys, record, metaState);
  },

  class_name(state, className) {
    state.className = className;
  },

  set_model(state, model) {
    state.record = model;
  },

  set_manage_model(state, payload) {
    state.autoUpdateManageModels[payload.className] = payload.model;
  },

  set_update_path(state, { basePath, saveLast }) {
    if (saveLast && state.autoUpdatePath) state.prevUpdatePaths.push(state.autoUpdatePath);
    if (basePath) state.autoUpdatePath = basePath;
    else state.autoUpdatePath = state.prevUpdatePaths.pop() || null;
  },

  set_new_order_customer(state, customer) {
    state.newOrderCustomer = customer;
  },

  add_update_changes(state, changeset) {
    if (changeset && changeset.length) state.autoUpdateChanges.push(...changeset);
  },

  set_selected_record_ids(state, recordIds) {
    state.selectedRecordIds = recordIds;
  },

  set_search_in_flight(state, bool) {
    state.searchInFlight = bool;
  },

  set_update_in_flight(state, { flag, className = null, autoUpdateID = null }) {
    state.autoUpdateInFlight = flag;
    if (className) {
      if (autoUpdateID) state.autoUpdateIDs[className] = autoUpdateID;
      else _unset(state.autoUpdateIDs, className);
    }
  },

  set_auto_update_flag(state, flag) {
    state.autoUpdateInFlight = flag;
  },

  save_reminder(state, reminderState) {
    state.saveReminder = reminderState;
  },

  rollback_state(state, prevoiusState) {
    state.record = prevoiusState;
  },

  set_dirty_flag(state, flag) {
    state.isRecordDirty = flag;
  },

  set_save_in_flight(state, bool) {
    state.saveInFlight = bool;
  },

  toggle_context_menu(state, payload = {}) {
    const { show = true, event, menuItems } = payload;
    state.showContextMenu = show;
    if (event) state.contextMenuPosition = { x: event.clientX, y: event.clientY };
    if (menuItems) state.contextMenuItems = menuItems;
  },

  set_copy_record_config(state, config) {
    state.copyRecordConfig = config;
  },
};

export default {
  namespaced: true,
  state: localState,
  getters: localGetters,
  actions: localActions,
  mutations: localMutations,
};
